import { useState, useEffect, useCallback, useReducer, useRef } from 'react';
import { supa } from './lib/supabase.js';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
// "as CapApp" perché il componente principale di questo file si chiama già App — vedi listener
// "appUrlOpen" più sotto, per il deep link di ritorno dopo il pagamento Stripe (02/09/2026).
import { App as CapApp } from '@capacitor/app';
import { TH, SWIPE_SCREENS, INIT_FILTERS, MONTHLY_GOALS, QUARTERLY_GOALS, REWARDS_CATALOG, DAILY_GOALS_POOL } from './lib/constants.js';
import { YR, DOY, TODAY, recomputeToday } from './lib/dateState.js';
import { shelfDragFlag, textWritingFlag } from './lib/gestureFlags.js';
import { isInsideHScroll, pushToast, LS, computeMetrics, goalProgress, getSeasonForMonth, pickDailyGoals } from './lib/helpers.js';
import { fetchRealFeed, fetchMyMicroBooks, fetchMySharedBooks, fetchActiveEvents, fetchPendingInvites, fetchFollowedMicroPages, fetchUnreadThreadCount, fetchCommunityFilters, fetchMemoryForToday, fetchNotifications, fetchUnreadNotifCount, fetchRewards, updateRewards, updateProfileTheme, savePushToken, notifyGoalUnlocked } from './lib/api.js';
import { reducer } from './reducer.js';
import { Btn, Logo, AnimatedSplash } from './components/Basic.jsx';
import { ToastDisplay, BNav, mainNavItems } from './components/Navigation.jsx';
import { FeedView } from './views/Feed.jsx';
import { CaptureView } from './views/Capture.jsx';
import { LibraryView } from './views/Library.jsx';
import { MicroView } from './views/Micro.jsx';
import { NotebookView } from './views/Notebook.jsx';
import { DiscoverView } from './views/Discover.jsx';
import { FiltersView, FilterCreateView } from './views/Filters.jsx';
import { YearCloseView } from './views/YearClose.jsx';
import { OncerFutureView } from './views/OncerFuture.jsx';
import { GoalsView, RewardsView } from './views/Goals.jsx';
import { ProfileView } from './views/Profile.jsx';
import { SettingsView } from './views/Settings.jsx';
import { ShelfEditorView } from './views/ShelfEditor.jsx';
import { PrivacyPolicyView, TermsView } from './views/LegalPages.jsx';
import { SubscriptionView } from './views/Subscription.jsx';
import { PrintPreviewView, PrintFormatView, PrintOrderView, PrintHubView } from './views/Print.jsx';
import { PasswordRecoveryView, Onboarding } from './views/Auth.jsx';
import { NotificationsView } from './views/Notifications.jsx';
import { ModerationView } from './views/Moderation.jsx';
import { GuidedTour } from './views/GuidedTour.jsx';
import { t as tr } from './lib/i18n.js';
// ── Transizione tra le 6 sezioni principali: rivelazione a "pagina che si
// stacca", col bordo disegnato come una vera curva vettoriale (Catmull-Rom
// → bezier) invece che a segmenti dritti — niente gradini a nessun livello
// di zoom, perché non è fatta di rettangoli discreti, è geometria continua.
// Si piega di più vicino al punto in cui si trascina, meno lontano da lì.
function smoothPathD(pts){
  if(pts.length<2) return '';
  // Costruisce un array di pezzi e li unisce una sola volta alla fine (join)
  // invece di concatenare stringhe ad ogni giro — più leggero per il motore
  // JS quando gira ad ogni fotogramma durante un trascinamento.
  const parts=[`M${pts[0][0]|0},${pts[0][1]|0}`];
  for(let i=0;i<pts.length-1;i++){
    const p0=pts[i-1]||pts[i], p1=pts[i], p2=pts[i+1], p3=pts[i+2]||p2;
    const c1x=p1[0]+(p2[0]-p0[0])/6, c1y=p1[1]+(p2[1]-p0[1])/6;
    const c2x=p2[0]-(p3[0]-p1[0])/6, c2y=p2[1]-(p3[1]-p1[1])/6;
    parts.push(`C${c1x|0},${c1y|0} ${c2x|0},${c2y|0} ${p2[0]|0},${p2[1]|0}`);
  }
  return parts.join(' ');
}
// Sezione stampa non ancora pronta per gli utenti — il lavoro sotto resta
// intatto (creazione libri, calcolo prezzi), semplicemente non ci si arriva
// da qui finché non è davvero pronta da mostrare (prezzi veri, non segnaposto).
function PrintComingSoonView({t,go}){
  return(
    <div className="screen fade" style={{background:t.bg,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:30,textAlign:"center"}}>
      <div style={{fontSize:50,marginBottom:16}}>🖨</div>
      <div style={{fontSize:18,fontWeight:800,color:t.tx,fontFamily:"Inter,sans-serif",marginBottom:8}}>{tr("app_print_soon_title")}</div>
      <div style={{fontSize:13,color:t.soft,fontFamily:"Inter,sans-serif",lineHeight:1.6,marginBottom:22}}>{tr("app_print_soon_body_1")}<br/>{tr("app_print_soon_body_2")}</div>
      <div onClick={()=>go("feed")} style={{padding:"12px 26px",borderRadius:40,background:t.pill,color:"#fff",fontSize:13,fontWeight:700,fontFamily:"Inter,sans-serif",cursor:"pointer"}}>{tr("app_print_soon_cta")}</div>
    </div>
  );
}
export default function App(){
  // authStage: "checking" (verifying session on load) → "out" (show Onboarding) → "ready" (main app)
  const [authStage,setAuthStage]=useState("checking");
  const [session,setSession]=useState(null);
  const [screen,setScreen]=useState("feed");
  const [viewUser,setViewUser]=useState(null);
  const [viewLibraryUser,setViewLibraryUser]=useState(null);
  const [openMicroKey,setOpenMicroKey]=useState(null);
  // Quando si arriva a Capture dal pulsante "Scatta oggi" DENTRO un
  // micro-libro o un libro condiviso (non dal tab "Scatta" principale),
  // qui viene ricordato quale libro era — così Capture può aprirsi già
  // in modalità "solo per quel libro", senza toccare il diario personale
  // a meno che l'utente non lo scelga esplicitamente.
  const [capturePreset,setCapturePreset]=useState(null);
  const [openUser,setOpenUser]=useState(null);
  const [discoverTab,setDiscoverTab]=useState("persone");
  const [printBook,setPrintBook]=useState(null);
  // Barra di navigazione — 31/08/2026: un'unica istanza, ferma sopra a
  // tutte le 6 sezioni (vedi il blocco che disegna SWIPE_SCREENS più sotto),
  // non più una copia dentro ogni singola vista. Prima ce n'era una per
  // sezione: durante il cambio-sezione la curva "tagliava" letteralmente la
  // barra a metà invece di farla restare ferma sopra al passaggio —
  // segnalato da Matte il 31/08/2026 ("la curva non passa sotto alla
  // barra"). captureNavVisible/profileNavTheme arrivano dalle rispettive
  // viste via callback, perché solo LORO sanno quando nascondersi (Cattura,
  // mentre si scrive) o con quale tema colorarsi (Profilo, quello di chi si
  // sta guardando) — App.jsx non ha modo di calcolarlo da solo.
  const [captureNavVisible,setCaptureNavVisible]=useState(true);
  const [profileNavTheme,setProfileNavTheme]=useState(null);
  const [printFormat,setPrintFormat]=useState(null); // scelta stile/taglia/orientamento dal wizard PrintFormatView
  const [highlightFilterId,setHighlightFilterId]=useState(null);
  const [,setMidnightTick]=useState(0); // bumped only to force a re-render after the day actually rolls over
  const [showSwipeHint,setShowSwipeHint]=useState(false);
  const [showGuidedTour,setShowGuidedTour]=useState(false);
  const [passwordRecovery,setPasswordRecovery]=useState(false);
  // True when we found a confirmed session but no profile row yet — happens
  // right after someone clicks the email confirmation link. Sends them
  // straight into username/theme creation instead of the welcome screen.
  const [needsProfileSetup,setNeedsProfileSetup]=useState(false);
  // Tempo minimo (ms) in cui resta visibile la schermata di caricamento
  // "Oncer" all'avvio, indipendentemente da quanto è veloce la verifica
  // della sessione — evita che sfarfalli su connessioni rapide e sostituisce,
  // di fatto, lo splash nativo (reso invisibile lato Android).
  //
  // Fix 02/09/2026 (Matte: "alcune frasi non si riescono a leggere"): da
  // quando la scritta finale è una tra sette frasi di lunghezza diversa
  // (dailyTagline in lib/constants.js), un valore fisso di 2200ms bastava
  // per le frasi corte ma tagliava a metà quelle più lunghe — corretto
  // allora calcolando il tempo minimo dalla lunghezza della frase di oggi
  // (fine battitura teorica + pausa).
  //
  // Fix 03/09/2026 (Matte: "vedo ancora frasi che non si completano in
  // questi giorni" — il fix sopra non bastava): quel calcolo assumeva che i
  // 40+ timer a catena della macchina da scrivere (uno ogni 42ms, vedi
  // AnimatedSplash) scorressero sempre a velocità costante. Ma la battitura
  // gira ESATTAMENTE nello stesso momento in cui loadUserData() qui sotto
  // carica feed/libri/eventi/inviti dal server — se il telefono è occupato
  // in quel momento (rete lenta, dispositivo poco potente, tanti dati da
  // processare), i singoli setTimeout della catena possono accumulare
  // ritardo rispetto al calcolo teorico, mentre finishSplash() partiva
  // comunque al tempo "giusto sulla carta" e tagliava la frase reale.
  // Anziché indovinare quanto ci metterà la battitura, ora aspettiamo un
  // segnale VERO di fine scrittura da AnimatedSplash (onDone) — corretto per
  // costruzione, qualunque sia la frase, il dispositivo o quanto sia lento
  // il caricamento dati in quel momento. SPLASH_MIN_MS resta solo come
  // tempo minimo "decorativo" (copre l'introduzione fissa: contorno,
  // riempimento, pallino, nome "Oncer"), non più legato alla frase del giorno.
  const SPLASH_MIN_MS=1200;
  const splashStartRef=useRef(Date.now());
  const splashTypingDoneRef=useRef(false); // impostato da AnimatedSplash tramite onDone, mai da un calcolo a priori
  const finishSplash=(fn)=>{
    const check=()=>{
      const elapsed=Date.now()-splashStartRef.current;
      if(splashTypingDoneRef.current&&elapsed>=SPLASH_MIN_MS){ fn(); return; }
      setTimeout(check,50);
    };
    check();
  };
  const [st,dispatch]=useReducer(reducer,null,()=>{
    // Feed is never read from or written to localStorage anymore — it's
    // always fetched fresh from the database. Clear out any old oversized
    // copy still sitting there from before this fix, so it stops eating
    // into the quota other keys need.
    try{ localStorage.removeItem("oncer_feed"); }catch(e){}
    return {
    myTheme: LS.g("oncer_theme","neon"),
    profile: LS.g("oncer_profile",{username:"tu",bio:""}),
    books:   LS.g("oncer_books",{[YR]:{pages:[]}}),
    micro:   LS.g("oncer_micro",[]),
    feed:    [],
    threads: LS.g("oncer_threads",[]),
    nbStyle: LS.g("oncer_nbstyle","cursive"),
    nbPaper: LS.g("oncer_nbpaper","ivory"),
    settings: LS.g("oncer_settings",{pushNotif:true,emailNotif:false,notifTime:"20:00",whoCanMessage:"everyone",libraryPublic:false,plan:"free"}),
    filters: LS.g("oncer_filters",INIT_FILTERS),
    rewards: LS.g("oncer_rewards",{unlocked:[],claimed:[],equippedDecorations:[],dailyStreak:{count:0,lastDay:null,lastYear:null}}),
    stats: LS.g("oncer_stats",{reactionsGivenToday:0,reactionsGivenDate:null}),
    shelfOrder: LS.g("oncer_shelforder",[]),
    unreadThreadsReal: 0,
    notifications: [],
    unreadNotifCount: 0,
    lastFeedViewedAt: LS.g("oncer_feedviewed",0),
    toast: null,
    memory: null,
    sharedBooks: [],
    activeEvents: [],
    pendingInvites: [],
    followedMicroPages: [],
    seenMicroPageIds: LS.g("oncer_seen_micro_pages",[]),
  };});
  // Best-effort reminder: fires a real system notification if the browser
  // tab is open (even backgrounded) past the person's chosen time and
  // today's page is still empty. Won't reach anyone once the tab/browser is
  // fully closed — that needs a server-side push system, a separate project.
  useEffect(()=>{
    let iv=null;
    try{
      if(!(st&&st.settings&&st.settings.pushNotif)) return;
      const check=()=>{
        try{
          const todayDone=st.books&&st.books[YR]&&st.books[YR].pages&&st.books[YR].pages.some(p=>p.day===DOY);
          if(todayDone) return;
          const notifTime=(st.settings&&st.settings.notifTime)||"20:00";
          const parts=notifTime.split(":").map(Number);
          const h=parts[0],m=parts[1];
          const now=new Date();
          if(now.getHours()*60+now.getMinutes()<h*60+m) return;
          const firedKey=`oncer_notif_fired_${YR}_${DOY}`;
          if(localStorage.getItem(firedKey)) return;
          if(!("Notification" in window)) return;
          const fire=()=>{
            try{ new Notification("Oncer",{body:tr("app_notif_reminder_body"),icon:"/favicon.ico"}); }catch(e){}
            try{ localStorage.setItem(firedKey,"1"); }catch(e){}
          };
          if(Notification.permission==="granted") fire();
          else if(Notification.permission!=="denied"&&typeof Notification.requestPermission==="function"){
            const req=Notification.requestPermission();
            if(req&&typeof req.then==="function") req.then(p=>{ if(p==="granted") fire(); }).catch(()=>{});
          }
        }catch(innerErr){ console.error("Oncer: controllo promemoria fallito —",innerErr.message); }
      };
      check();
      iv=setInterval(check,60000);
    }catch(outerErr){ console.error("Oncer: impostazione promemoria fallita —",outerErr.message); }
    return ()=>{ if(iv) clearInterval(iv); };
  },[st&&st.settings,st&&st.books]);
  const [flip,setFlip]=useState(null); // {dir,target} mentre una transizione sta suonando; null a riposo
  // ── Le 6 sezioni principali restano SEMPRE montate, tutte e sei, per
  // tutta la sessione — non più due "posti" che si scambiano il contenuto.
  // "screen" da solo decide quale delle 6 è quella attiva/visibile
  // (isActive = name===screen più sotto, nel render): passare da una
  // all'altra non monta né smonta mai nulla, si limita a rendere visibile
  // quella giusta e a nascondere quella lasciata. Zero rimontaggi, zero
  // nuove chiamate di rete, in QUALUNQUE ordine e dopo quanto tempo vuoi —
  // sono solo 6 sezioni leggere, il costo di tenerle vive tutte insieme è
  // accettabile su un telefono di oggi.
  // Le schermate FUORI dalle 6 (impostazioni, notifiche, un profilo aperto,
  // filtri, ecc. — sono più di dieci) restano invece un semplice overlay
  // sopra le 6, montato solo quando serve e smontato quando si torna a una
  // delle 6: sono raggiunte con un tocco fisico (non uno scorrimento), non
  // hanno bisogno della curva né di restare calde — va bene ricaricarle
  // ogni volta, come confermato esplicitamente.
  const sectionRootRef=useRef(null); // misura larghezza/altezza reali dello schermo
  // screenRef è lo specchio SEMPRE aggiornato di "screen", scritto nello
  // STESSO istante in cui si chiama setScreen (mai un giro dopo tramite
  // useEffect) — se si tocca di nuovo lo schermo prima che React abbia
  // ridisegnato (swipe veloci uno via l'altro), il calcolo della prossima
  // sezione deve partire da un valore fresco, non da quello di un render
  // passato: era proprio questo lo scarto dietro "a volte va avanti, a
  // volte torna indietro".
  const screenRef=useRef(screen);
  const setScreenSync=(v)=>{ screenRef.current=v; setScreen(v); };
  // Quale delle 6 sezioni con la curva è quella VISIBILE in questo momento
  // (clip-path:none) — non sempre coincide con "screen": se sei su una
  // schermata secondaria (impostazioni, notifiche...) "screen" dice quella,
  // ma sotto, coperta, resta comunque visibile l'ultima delle 6 su cui
  // stavi. Serve per sapere sempre CHI nascondere quando se ne rivela
  // un'altra delle 6 — altrimenti resterebbero visibili due sezioni
  // sovrapposte contemporaneamente.
  const activeMainRef=useRef("feed");
  // vel = velocità orizzontale del dito, in pixel/millisecondo, aggiornata a
  // ogni movimento e smussata (media mobile) per non risentire di un singolo
  // campione rumoroso — usata al rilascio per riconoscere un colpo deciso
  // ("flick") anche quando non ha percorso molta strada: vedi finishDragWipe.
  const wipeDragRef=useRef({dragging:false,decided:false,startX:0,startY:0,dir:null,target:null,touchY:0,dx:0,ignore:false,vel:null,lastMoveT:0,lastMoveX:0});
  const wipeGeomRef=useRef({W:0,H:0});
  // Un nodo DOM per ciascuna delle 6 sezioni (chiave = nome), più il filo
  // decorativo della curva. Popolato dai collegamenti stabili qui sotto.
  const wipeElsRef=useRef({dividerPath:null});
  // Collegamenti (ref) STABILI, creati una sola volta ciascuno — mai una
  // funzione freccia nuova ad ogni ridisegno dentro il JSX. Con una
  // funzione nuova ad ogni render, React stacca (chiama con null) e
  // riattacca il vecchio collegamento ad ogni ridisegno, ANCHE se il nodo
  // HTML reale è sempre lo stesso — capitando nel momento sbagliato
  // (soprattutto su Safari, che gestisce questi tempi diversamente da
  // Chrome), l'animazione scriveva su un riferimento momentaneamente vuoto
  // e non succedeva nulla, mentre il cambio di sezione (un pezzo di codice
  // indipendente) andava comunque a segno: da qui "a volte si blocca senza
  // animazione". Costruiti una volta sola con useRef, non ricreati mai.
  const screenRefCallbacks=useRef(
    Object.fromEntries(SWIPE_SCREENS.map(name=>[name,(el)=>{ wipeElsRef.current[name]=el; }]))
  ).current;
  const setDividerRef=useCallback(el=>{ wipeElsRef.current.dividerPath=el; },[]);
  const wipeRafRef=useRef(null);
  const dividerSettleTimeoutRef=useRef(null);
  const pendingApplyRef=useRef(false);
  const lastApplyRef=useRef({dx:0,touchY:0,target:null});
  // Nasconde subito la sezione indicata, prima che una curva cominci ad
  // animarla — altrimenti, se quella sezione era stata attiva in passato,
  // porta ancora addosso il ritaglio di allora (pienamente visibile), e
  // lampeggerebbe intera per un istante prima che la curva parta davvero.
  const resetLayerHidden=(name)=>{
    const node=wipeElsRef.current[name];
    if(node) node.style.clipPath='inset(0 100% 0 0)';
  };
  // Disegna il bordo curvo e ritaglia la sezione CHE STA ARRIVANDO (target,
  // passato esplicitamente da chi chiama — non più dedotto da "quale dei
  // due posti non è attivo", perché ora le sezioni sono sei, non due) —
  // chiamata sia dal trascinamento dal vivo sia dall'animazione automatica
  // quando si tocca la barra in basso.
  const applyWipeVisual=(dx,touchY,target)=>{
    const node=wipeElsRef.current[target];
    if(!node) return;
    const {W,H}=wipeGeomRef.current;
    if(!W||!H) return;
    const dir=wipeDragRef.current.dir||-1;
    const frac=Math.max(0,Math.min(1,Math.abs(dx)/W));
    const divider=wipeElsRef.current.dividerPath;
    if(frac<=0.001){
      node.style.clipPath='inset(0 100% 0 0)';
      if(divider) divider.setAttribute('opacity','0');
      return;
    }
    const bendAmp=frac*W*0.15;
    // Più stretto di prima (era H*0.3): la piega resta aderente al punto
    // esatto del dito invece di allargarsi su una zona ampia e "morbida" —
    // così si vede chiaramente che segue dove tocchi, non un'area vaga.
    const sigma=H*0.16;
    const n=13; // meno punti = meno lavoro per frame, resta liscia grazie allo smoothing
    // La curva CRESCE (rivela sempre più la sezione in arrivo) — bordo
    // "fisso" sul lato opposto a quello da cui si trascina, così la parte
    // già rivelata resta ferma e visibile mentre il resto continua a crescere.
    const flatX=dir<0?W:0;
    const baseX=dir<0?W*(1-frac):W*frac;
    const pts=[];
    for(let i=0;i<=n;i++){
      const y=H*i/n;
      const g=Math.exp(-((y-touchY)*(y-touchY))/(2*sigma*sigma));
      const bx=baseX+(dir<0?1:-1)*bendAmp*g;
      pts.push([bx,y]);
    }
    const last=pts[pts.length-1];
    let d=`M${flatX|0},0 L${flatX|0},${H|0} L${last[0]|0},${last[1]|0} `;
    d+=smoothPathD(pts.slice().reverse()).replace(/^M[^ ]+ /,'')+' Z';
    node.style.clipPath=`path('${d}')`;
    // Filo sottile del colore del tema lungo il bordo che si piega — solo
    // decorativo, non incide sul ritaglio: riusa gli stessi punti già
    // calcolati sopra, nessun lavoro aggiuntivo di rilievo.
    if(divider){
      divider.setAttribute('d',smoothPathD(pts));
      divider.setAttribute('opacity','1');
    }
  };
  // Raggruppa gli aggiornamenti del tocco grezzo (che possono arrivare più
  // spesso del refresh dello schermo) su un solo aggiornamento per frame —
  // è questo, insieme a scrivere direttamente sul DOM invece che passare da
  // uno stato React, che elimina i micro-scatti durante lo scorrimento.
  const scheduleApply=(dx,touchY,target)=>{
    lastApplyRef.current={dx,touchY,target};
    if(pendingApplyRef.current) return;
    pendingApplyRef.current=true;
    requestAnimationFrame(()=>{
      pendingApplyRef.current=false;
      // Se il dito si è già sollevato nel frattempo (dr.dragging tornato
      // falso), questo aggiornamento è "vecchio" — il gesto è già passato a
      // finishDragWipe, che sta animando la chiusura vera. Applicarlo lo
      // stesso lo interromperebbe a metà con un valore superato: era
      // proprio questa la corsa critica dietro "resta a metà".
      if(!wipeDragRef.current.dragging) return;
      const la=lastApplyRef.current;
      applyWipeVisual(la.dx,la.touchY,la.target);
    });
  };
  // Nasconde di nuovo la sezione appena lasciata — chiamata a fine
  // transizione (completata o annullata) sia dal trascinamento dal vivo sia
  // dall'animazione automatica. Con le 6 sezioni sempre montate, chi
  // diventa inattiva deve tornare esplicitamente al ritaglio nascosto: lo
  // stile scritto a mano sul nodo (clip-path) vince sempre sulla classe CSS
  // che vorrebbe nasconderla, quindi va fatto qui, non lasciato al CSS.
  const hideLayer=(name)=>{
    if(!name||!SWIPE_SCREENS.includes(name)) return;
    const node=wipeElsRef.current[name];
    if(node) node.style.clipPath='inset(0 100% 0 0)';
  };
  const showLayer=(name)=>{
    const node=wipeElsRef.current[name];
    if(node) node.style.clipPath='none';
  };
  // A fine transizione COMPLETATA (non annullata) il filo decorativo della
  // curva resta disegnato alla sua piega più ampia — bendAmp in
  // applyWipeVisual è al MASSIMO proprio quando frac arriva a 1, non a
  // zero — e l'SVG che lo contiene si smonta di colpo appena flip torna
  // null. Il risultato è che il filo sparisce ancora piegato: è esattamente
  // lo "scatto" residuo segnalato.
  // Qui invece la piega viene fatta rientrare a dritta con una VERA
  // animazione (stessa formula di applyWipeVisual, bendAmp che scende da
  // W*0.15 — il suo valore massimo, raggiunto esattamente a frac=1 — fino a
  // 0), riusando touchY e dir del gesto appena finito così il filo resta
  // sempre nel punto giusto (baseX=dir<0?0:W, il bordo dove la curva è
  // arrivata DAVVERO — non "il lato fermo opposto", errore della versione
  // precedente che faceva comparire una riga dritta dal lato sbagliato).
  // Solo quando è già dritto parte la dissolvenza CSS, e solo dopo che è
  // invisibile si smonta l'SVG (setFlip(null)) — niente più scatti in
  // nessuna delle due fasi.
  const settleDivider=(dir,touchY)=>{
    const divider=wipeElsRef.current.dividerPath;
    const {W,H}=wipeGeomRef.current;
    if(dividerSettleTimeoutRef.current){ clearTimeout(dividerSettleTimeoutRef.current); dividerSettleTimeoutRef.current=null; }
    if(!divider||!W||!H){ setFlip(null); return; }
    const n=13, sigma=H*0.16, startBend=W*0.15, baseX=dir<0?0:W;
    const t0=performance.now();
    const UNBEND_DUR=140;
    const stepUnbend=(now)=>{
      const p=Math.min(1,(now-t0)/UNBEND_DUR);
      const ease=1-Math.pow(1-p,2);
      const bendAmp=startBend*(1-ease);
      const pts=[];
      for(let i=0;i<=n;i++){
        const y=H*i/n;
        const g=Math.exp(-((y-touchY)*(y-touchY))/(2*sigma*sigma));
        pts.push([baseX+(dir<0?1:-1)*bendAmp*g,y]);
      }
      divider.setAttribute('d',smoothPathD(pts));
      if(p<1){ wipeRafRef.current=requestAnimationFrame(stepUnbend); }
      else {
        divider.setAttribute('opacity','0'); // ora è dritto: la dissolvenza CSS parte da una linea ferma, non da una curva
        dividerSettleTimeoutRef.current=setTimeout(()=>{ dividerSettleTimeoutRef.current=null; setFlip(null); },160);
      }
    };
    wipeRafRef.current=requestAnimationFrame(stepUnbend);
  };
  // Al rilascio del dito: se si è trascinato oltre la soglia, completa la
  // rivelazione e cambia sezione davvero; altrimenti torna indietro
  // dolcemente e resta dove si era. In entrambi i casi parte da dove si
  // trovava davvero il dito, non da un valore fisso.
  const finishDragWipe=(dr,endClientX)=>{
    const {W}=wipeGeomRef.current;
    const dx=endClientX-dr.startX;
    // Due modi di "meritare" il completamento, non uno solo:
    //  1) trascinato oltre il 18% dello schermo — un gesto deliberato anche
    //     se lento, senza dover arrivare quasi in fondo (prima serviva il
    //     30%, troppo per sentirsi "veloce" e naturale).
    //  2) oppure un colpo deciso (flick) nella direzione giusta, qualunque
    //     sia la distanza percorsa — è esattamente il caso di un movimento
    //     "non completo": il dito parte, si muove chiaramente e veloce in
    //     una direzione, e si stacca prima di arrivare in fondo. Deve
    //     comunque proseguire da solo fino alla sezione successiva, non
    //     tornare indietro. dr.vel è già nella stessa direzione di dx per
    //     costruzione (si accumula solo mentre il trascinamento è "deciso",
    //     cioè già chiaramente orizzontale in quel verso).
    const FLICK_VEL=0.5; // px/ms — un colpo netto, non un trascinamento lento
    const isFlick=dr.vel!=null&&Math.abs(dr.vel)>FLICK_VEL&&Math.sign(dr.vel)===Math.sign(dx);
    const committed=W>0&&(Math.abs(dx)/W>0.18||isFlick);
    const target=dr.target, touchY=dr.touchY;
    const leaving=activeMainRef.current; // la sezione che si sta (forse) lasciando
    const dir=wipeDragRef.current.dir;
    const startFrac=Math.min(1,Math.abs(dx)/(W||1));
    const t0=performance.now();
    const DUR=committed?170:210;
    if(wipeRafRef.current) cancelAnimationFrame(wipeRafRef.current);
    const step=(now)=>{
      const p=Math.min(1,(now-t0)/DUR);
      const ease=1-Math.pow(1-p,3);
      const frac=committed?(startFrac+(1-startFrac)*ease):(startFrac*(1-ease));
      const curDx=dir<0?-frac*W:frac*W;
      applyWipeVisual(curDx,touchY,target);
      if(p<1){ wipeRafRef.current=requestAnimationFrame(step); }
      else {
        if(committed){
          // Rete di sicurezza: garantisce piena visibilità/nascondimento
          // indipendentemente da qualunque scrittura in ritardo — non ci si
          // fida che l'ultimo fotogramma dell'animazione sia arrivato per primo.
          showLayer(target);
          if(leaving!==target) hideLayer(leaving);
          activeMainRef.current=target;
          setScreenSync(target);
          settleDivider(dir,touchY);
        } else {
          // Annullato: frac è già tornato verso 0 da solo durante
          // l'animazione, quindi il filo si è già dissolto naturalmente
          // dentro applyWipeVisual — smontaggio immediato, niente da sistemare.
          setFlip(null);
        }
      }
    };
    wipeRafRef.current=requestAnimationFrame(step);
  };
  // Quando la transizione parte da un TOCCO sulla barra in basso (non da un
  // trascinamento — lì niente dito da seguire) la anima da sola, stesso
  // disegno del bordo, partendo dal centro dello schermo.
  useEffect(()=>{
    if(!flip) return;
    if(wipeDragRef.current.dragging) return; // un trascinamento dal vivo sta già guidando l'animazione
    const {W,H}=wipeGeomRef.current;
    const target=flip.target;
    const leaving=activeMainRef.current;
    if(!W||!H){
      showLayer(target);
      if(leaving!==target) hideLayer(leaving);
      activeMainRef.current=target;
      setScreenSync(target); setFlip(null);
      return;
    }
    const dir=flip.dir==="next"?-1:1;
    wipeDragRef.current.dir=dir;
    const touchY=H/2;
    const t0=performance.now();
    const DUR=380;
    let raf;
    const step=(now)=>{
      const p=Math.min(1,(now-t0)/DUR);
      const ease=1-Math.pow(1-p,3);
      const dx=dir<0?-ease*W:ease*W;
      applyWipeVisual(dx,touchY,target);
      if(p<1){ raf=requestAnimationFrame(step); wipeRafRef.current=raf; }
      else {
        showLayer(target); // stessa rete di sicurezza del trascinamento dal vivo
        if(leaving!==target) hideLayer(leaving);
        activeMainRef.current=target;
        setScreenSync(target);
        settleDivider(dir,touchY); // stesso allineamento-e-dissolvenza del trascinamento dal vivo
      }
    };
    raf=requestAnimationFrame(step);
    wipeRafRef.current=raf;
    return ()=>{ if(raf) cancelAnimationFrame(raf); };
  },[flip]);
  // Listener nativo (non sintetico React) sul touchmove: solo così si può
  // bloccare davvero lo scroll verticale una volta deciso che il gesto è un
  // trascinamento orizzontale — i listener React sono passivi di default e
  // preventDefault() lì non avrebbe alcun effetto reale.
  // NOTA: attaccato tramite ref-callback, non tramite useEffect(...,[]) — la
  // shell con questo div nasce solo quando authStage diventa "ready" (prima
  // c'è lo splash/l'Onboarding), quindi un effetto con dipendenze vuote,
  // eseguito una sola volta all'avvio dell'intero componente App, la
  // troverebbe sempre assente e non si riattaccherebbe mai più: era esattamente
  // questo il motivo per cui il trascinamento dal vivo non partiva mai sul
  // dispositivo vero, e si vedeva solo l'animazione automatica di riserva a
  // fine gesto. La ref-callback invece si attiva esattamente quando il nodo
  // vero viene creato, qualunque sia lo stato dell'app in quel momento.
  const touchMoveCleanupRef=useRef(null);
  const handleSectionTouchMove=(e)=>{
    const dr=wipeDragRef.current;
    if(!dr.dragging||dr.ignore) return;
    const root=sectionRootRef.current;
    if(!root) return;
    const tt=e.touches[0];
    const dx=tt.clientX-dr.startX;
    const dy=tt.clientY-dr.startY;
    if(!dr.decided){
      // Più larga di prima (era 6px): sotto questa soglia non si monta
      // ancora la schermata di arrivo — alcune (es. Feed) fanno chiamate di
      // rete vere al montaggio, quindi un tocco minimo/accidentale non deve
      // innescarle. Restando comunque piccola, non si sente come un ritardo:
      // è solo l'inizio del gesto, prima che tu stia davvero trascinando.
      if(Math.abs(dx)<40) return;
      if(Math.abs(dx)<Math.abs(dy)*1.4) return; // non ancora chiaramente orizzontale — si ricontrolla al prossimo movimento
      const curScreen=screenRef.current;
      if(!SWIPE_SCREENS.includes(curScreen)) return; // fuori dalle 6 sezioni: nessuna anteprima dal vivo, si decide tutto al rilascio come prima
      const idx=SWIPE_SCREENS.indexOf(curScreen);
      const dir=dx<0?-1:1;
      const target=dir<0?SWIPE_SCREENS[idx+1]:SWIPE_SCREENS[idx-1];
      if(!target) return; // già all'estremo, nessuna sezione in quella direzione
      dr.decided=true; dr.dir=dir; dr.target=target;
      const rect=root.getBoundingClientRect();
      // Misurata una volta sola qui, MAI più per il resto del gesto — prima
      // veniva ricalcolata ad ogni singolo movimento del dito, il che forza
      // il browser a ricalcolare il layout ogni volta invece di limitarsi a
      // disegnare: è questo che rendeva il trascinamento lento e impreciso.
      wipeGeomRef.current={W:rect.width,H:rect.height,top:rect.top};
      wipeDragRef.current.dir=dir;
      dr.touchY=tt.clientY-rect.top;
      // Non c'è più nulla da "montare" qui: le 6 sezioni sono già tutte
      // pronte in permanenza. Basta assicurarsi che quella in arrivo parta
      // nascosta prima che la curva cominci a rivelarla.
      resetLayerHidden(target);
      setFlip({dir:dir<0?"next":"prev",target});
    }
    if(dr.decided){
      if(e.cancelable) e.preventDefault();
      dr.dx=dx;
      dr.touchY=tt.clientY-wipeGeomRef.current.top;
      // Velocità istantanea da questo movimento al precedente, poi mescolata
      // (70% storico/30% nuovo) con quella già calcolata — un singolo evento
      // rumoroso (capita spesso su Android, dove i touchmove non arrivano a
      // intervalli regolari) non deve da solo decidere se è stato un colpo
      // deciso o no. Usata solo al rilascio, in finishDragWipe.
      const now=e.timeStamp||performance.now();
      const dt=now-dr.lastMoveT;
      if(dt>0&&dt<120){
        const instVel=(tt.clientX-dr.lastMoveX)/dt;
        dr.vel=dr.vel==null?instVel:dr.vel*0.7+instVel*0.3;
      }
      dr.lastMoveT=now; dr.lastMoveX=tt.clientX;
      scheduleApply(dr.dx,dr.touchY,dr.target);
    }
  };
  // Tiene wipeGeomRef sempre aggiornato DA SOLO, dal primo istante in cui il
  // contenitore esiste — non solo quando parte un gesto. La prima
  // transizione della sessione può capitare in un momento in cui il layout
  // non si è ancora del tutto assestato (font, aree di sicurezza del
  // telefono...): misurando solo lì, quella singola misura può risultare
  // imprecisa per un istante (è quello che ha causato la linea della curva
  // leggermente fuori posto la primissima volta su "Scatta"). Con un
  // ResizeObserver la misura si aggiorna da sé ogni volta che le dimensioni
  // vere cambiano — al montaggio, dopo un caricamento font, ruotando il
  // telefono — quindi non serve più contare sul fatto che il gesto arrivi
  // "abbastanza tardi" perché il layout sia già pronto.
  const geomObserverCleanupRef=useRef(null);
  const setSectionRoot=useCallback((node)=>{
    if(touchMoveCleanupRef.current){ touchMoveCleanupRef.current(); touchMoveCleanupRef.current=null; }
    if(geomObserverCleanupRef.current){ geomObserverCleanupRef.current(); geomObserverCleanupRef.current=null; }
    sectionRootRef.current=node;
    if(node){
      const listener=(e)=>handleSectionTouchMove(e);
      node.addEventListener('touchmove',listener,{passive:false});
      touchMoveCleanupRef.current=()=>node.removeEventListener('touchmove',listener);
      const measure=()=>{
        const rect=node.getBoundingClientRect();
        wipeGeomRef.current={W:rect.width,H:rect.height,top:rect.top};
      };
      measure();
      if(typeof ResizeObserver!=="undefined"){
        const ro=new ResizeObserver(measure);
        ro.observe(node);
        geomObserverCleanupRef.current=()=>ro.disconnect();
      } else {
        // Riserva per WebView molto vecchie senza ResizeObserver: una
        // misura sola al montaggio è comunque meglio di zero.
        geomObserverCleanupRef.current=null;
      }
    }
  },[]);
  // Keeps "today" honest: the calendar day, never a rolling 24h window from
  // whenever the app happened to load. Two mechanisms, because mobile OSes
  // routinely suspend background timers while a phone is locked:
  //  1) a timer set for the exact moment of the next midnight, for anyone
  //     actively using the app when it strikes;
  //  2) a check whenever the app becomes visible again, which is what
  //     actually catches "left it open overnight, opened it this morning".
  useEffect(()=>{
    const checkForNewDay=()=>{
      const real=Math.ceil((new Date()-new Date(new Date().getFullYear(),0,1))/86400000);
      if(real!==DOY||new Date().getFullYear()!==YR){
        recomputeToday();
        setMidnightTick(t=>t+1);
      }
    };
    let midnightTimer=null;
    const scheduleMidnight=()=>{
      const now=new Date();
      const next=new Date(now.getFullYear(),now.getMonth(),now.getDate()+1,0,0,2); // 2s past midnight, safely clear of the boundary
      midnightTimer=setTimeout(()=>{ checkForNewDay(); scheduleMidnight(); },next-now);
    };
    scheduleMidnight();
    const onVisible=()=>{ if(document.visibilityState==="visible") checkForNewDay(); };
    document.addEventListener("visibilitychange",onVisible);
    window.addEventListener("focus",onVisible);
    return ()=>{
      clearTimeout(midnightTimer);
      document.removeEventListener("visibilitychange",onVisible);
      window.removeEventListener("focus",onVisible);
    };
  },[]);
  // On load: is there already a signed-in Supabase session? If so, fetch the
  // real profile + real pages and skip Onboarding. Also listen for sign-out
  // — and, importantly, for the session switching to a DIFFERENT account
  // while the app is already open (e.g. confirming a second account's email
  // in a new tab without ever logging out of the first). loadUserData is
  // reusable for exactly that reason: it runs from the very first check AND
  // every time onAuthStateChange reports a genuinely different user id.
  const loadedUidRef=useRef(null);
  useEffect(()=>{
    let mounted=true;
    const loadUserData=async(uid)=>{
      // Se questo browser/dispositivo aveva dati salvati in locale per UN
      // ALTRO account (es. un account diverso, ancora con sessione attiva,
      // usato prima sullo stesso dispositivo), quei dati vecchi — inclusa
      // la foto profilo e la copertina — non devono mai comparire sotto
      // questo account, nemmeno per un istante. Si ripulisce tutto appena
      // ci si accorge che l'account è cambiato, prima di caricare
      // qualunque altra cosa.
      let lastUid=null;
      try{ lastUid=localStorage.getItem("oncer_last_uid"); }catch(e){}
      if(lastUid&&lastUid!==uid){
        dispatch({type:"RESET_LOCAL_CACHE"});
      }
      try{ localStorage.setItem("oncer_last_uid",uid); }catch(e){}
      loadedUidRef.current=uid;
      const {data:prof,error:profErr}=await supa.from("profiles").select("*").eq("id",uid).single();
      console.log("Oncer: sessione per utente",uid,"— profilo:",prof?prof.username:"NON TROVATO",profErr?("— ERRORE: "+profErr.message):"");
      if(!prof){
        // Sessione valida (probabilmente appena confermata via email) ma
        // nessun profilo — manda dritti alla creazione username/tema
        // invece di caricare un'app "vuota" o rimandare a un login manuale.
        finishSplash(()=>{ if(mounted){ setNeedsProfileSetup(true); setAuthStage("out"); } });
        return;
      }
      if(mounted){
        dispatch({type:"SET_PROFILE",data:{username:prof.username,bio:prof.bio||"",avatarUrl:prof.avatar_url||null,coverUrl:prof.cover_url||null,isAdmin:!!prof.is_admin,verified:!!prof.verified}});
        dispatch({type:"SET_THEME",theme:prof.theme||"neon"});
      }
      // Tutte le letture qui sotto sono indipendenti tra loro — nessuna usa
      // il risultato di un'altra — quindi partono tutte insieme invece che
      // una dopo l'altra. Prima erano in fila (await uno alla volta): ogni
      // giro di rete in più si sommava ai precedenti, ed è quello che
      // portava il caricamento anche a 30-40 secondi. Ora il tempo totale
      // è quello della richiesta più lenta, non la somma di tutte.
      const [
        pagesRes,
        realFeedItems,
        realMicro,
        unreadCount,
        realRewards,
        notifItems,
        unreadNotifs,
        communityFilters,
        memory,
        sharedBooks,
        activeEvents,
        pendingInvites,
        followedMicro,
      ]=await Promise.all([
        // Solo le pagine del vero diario annuale (micro_book_id nullo) — le
        // foto scattate solo per un micro-libro non contano come "foto di
        // oggi" del diario personale, e non devono comparire qui.
        supa.from("pages").select("*").eq("user_id",uid).is("micro_book_id",null).order("day",{ascending:true}),
        fetchRealFeed(uid),
        fetchMyMicroBooks(uid),
        fetchUnreadThreadCount(uid),
        fetchRewards(uid),
        fetchNotifications(uid),
        fetchUnreadNotifCount(uid),
        fetchCommunityFilters(),
        fetchMemoryForToday(uid),
        fetchMySharedBooks(uid),
        fetchActiveEvents(),
        fetchPendingInvites(uid),
        fetchFollowedMicroPages(uid),
      ]);
      const {data:pageRows,error:pagesErr}=pagesRes;
      console.log("Oncer: caricamento pagine per utente",uid,"— trovate:",pageRows?pageRows.length:0,pagesErr?("— ERRORE: "+pagesErr.message):"");
      if(mounted){
        const booksByYear={};
        (pageRows||[]).forEach(r=>{
          if(!booksByYear[r.year]) booksByYear[r.year]={pages:[]};
          booksByYear[r.year].pages.push({photo:r.photo,caption:r.caption,day:r.day,ts:new Date(r.created_at).getTime(),filter:r.filter_id||"none",sticker:r.sticker_id||null});
        });
        if(!booksByYear[YR]) booksByYear[YR]={pages:[]};
        dispatch({type:"LOAD_PAGES",books:booksByYear});
      }
      console.log("Oncer: feed reale caricato —",realFeedItems.length,"post da utenti reali");
      if(mounted) dispatch({type:"LOAD_FEED",items:realFeedItems});
      console.log("Oncer: micro-libri reali caricati —",realMicro.length);
      if(mounted) dispatch({type:"LOAD_MICRO",items:realMicro});
      if(mounted) dispatch({type:"SET_UNREAD_THREADS",count:unreadCount});
      if(mounted) dispatch({type:"SET_REWARDS",rewards:realRewards});
      if(mounted) dispatch({type:"LOAD_NOTIFICATIONS",items:notifItems});
      if(mounted) dispatch({type:"SET_UNREAD_NOTIF_COUNT",count:unreadNotifs});
      console.log("Oncer: filtri community caricati —",communityFilters.length);
      if(mounted) dispatch({type:"LOAD_FILTERS",items:communityFilters});
      if(mounted) dispatch({type:"LOAD_MEMORY",memory});
      console.log("Oncer: libri condivisi caricati —",sharedBooks.length);
      if(mounted) dispatch({type:"LOAD_SHARED_BOOKS",items:sharedBooks});
      if(mounted) dispatch({type:"LOAD_EVENTS",items:activeEvents});
      if(mounted) dispatch({type:"LOAD_INVITES",items:pendingInvites});
      if(mounted) dispatch({type:"LOAD_FOLLOWED_MICRO",items:followedMicro});
      finishSplash(()=>{ if(mounted){ setAuthStage("ready"); } });
    };
    let initialCheckDone=false;
    console.log("Oncer DIAG: avvio, chiamo getSession()");
    supa.auth.getSession().then(({data})=>{
      if(!mounted) return;
      initialCheckDone=true;
      console.log("Oncer DIAG: getSession() risolta — sessione presente?",!!data.session);
      if(data.session){
        setSession(data.session);
        loadUserData(data.session.user.id);
      } else {
        console.log("Oncer DIAG: getSession() dice NESSUNA sessione — vado su authStage=out");
        finishSplash(()=>{ if(mounted) setAuthStage("out"); });
      }
    });
    const {data:listener}=supa.auth.onAuthStateChange((_event,newSession)=>{
      console.log("Oncer DIAG: onAuthStateChange —",_event,"— sessione presente?",!!newSession,"— initialCheckDone?",initialCheckDone);
      setSession(newSession);
      if(_event==="PASSWORD_RECOVERY") setPasswordRecovery(true);
      // Finché il controllo esplicito qui sopra non ha ancora finito la
      // primissima volta, ignoriamo qualunque segnale "nessuna sessione" da
      // qui — è lui il solo responsabile della primissima decisione, in modo
      // affidabile. Da questo punto in poi (dopo l'avvio) reagiamo solo ai
      // cambiamenti veri — uscita volontaria, cambio account — non a
      // possibili ritardi del dispositivo nel recuperare la sessione salvata
      // durante l'avvio dell'app.
      if(!initialCheckDone){ console.log("Oncer DIAG: ignorato perché initialCheckDone è ancora false"); return; }
      if(!newSession){ console.log("Oncer DIAG: onAuthStateChange dice NESSUNA sessione (dopo initialCheckDone) — vado su authStage=out"); setAuthStage("out"); return; }
      // Riconosce un account diverso da quello già caricato — capita
      // proprio nello scenario "confermo una seconda email senza essermi
      // scollegato dalla prima" — e ricarica tutto da capo per quello giusto.
      if(newSession.user.id!==loadedUidRef.current){
        loadUserData(newSession.user.id);
      }
    });
    return ()=>{ mounted=false; listener.subscription.unsubscribe(); };
  },[]);
  // go() resolves every navigation target to a concrete screen name (plus any
  // side effect it needs, e.g. picking which user's chat to open). When both
  // the current and target screens are among the six main sections, the swap
  // plays as an animated page-turn instead of an instant cut.
  const historyStack=useRef([]);
  // Se una transizione (curva trascinata o automatica) sta ancora animando
  // quando arriva una richiesta di navigazione nuova, non la si lascia
  // finire con calma: la si completa subito, sincronamente — "screen" e i
  // ritagli delle due sezioni coinvolte vanno TUTTI in uno stato coerente in
  // un solo istante, prima ancora di guardare dove si deve andare dopo. Così
  // tap o swipe ravvicinati saltano subito da una sezione all'altra invece
  // di accumularsi in una fila di animazioni lente, e "screen" non si
  // disallinea mai da quello che è VERAMENTE mostrato.
  const forceFinishFlip=(f)=>{
    if(!f) return;
    if(wipeRafRef.current){ cancelAnimationFrame(wipeRafRef.current); wipeRafRef.current=null; }
    // Se un nuovo gesto/tap interrompe proprio mentre il filo della curva
    // sta ancora dissolvendosi dalla transizione precedente (vedi
    // settleDivider), il timeout in sospeso va cancellato qui — altrimenti
    // scatterebbe più tardi chiamando setFlip(null) sopra il flip NUOVO
    // appena avviato da questa stessa chiamata, interrompendolo a metà.
    if(dividerSettleTimeoutRef.current){ clearTimeout(dividerSettleTimeoutRef.current); dividerSettleTimeoutRef.current=null; }
    const leaving=activeMainRef.current;
    showLayer(f.target);
    if(leaving!==f.target) hideLayer(leaving);
    activeMainRef.current=f.target;
    setScreenSync(f.target);
    setFlip(null);
  };
  const go=useCallback((s,opts)=>{
    // Se una transizione precedente è ancora a metà, la si chiude subito qui
    // (vedi forceFinishFlip sopra) — da qui in poi in questa chiamata si
    // ragiona sempre sullo stato FRESCO (screenRef/activeMainRef), mai su
    // "screen" catturato dal render precedente, che a questo punto potrebbe
    // già essere vecchio di un istante.
    if(flip) forceFinishFlip(flip);
    const curScreen=screenRef.current;
    console.log("Oncer: go() chiamata con",s,"— schermata attuale:",curScreen);
    let target=s, sideEffect=null;
    if(s.startsWith("profile_")){
      const u=s.replace("profile_","");
      target="profile";
      const isSelf=u==="me"||(st.profile.username&&u===st.profile.username);
      sideEffect=()=>setViewUser(isSelf?null:u);
    } else if(s.startsWith("library_")){
      const u=s.replace("library_","");
      target="library";
      const isSelf=u==="me"||(st.profile.username&&u===st.profile.username);
      sideEffect=()=>setViewLibraryUser(isSelf?null:u);
    } else if(s==="library"){
      target="library";
      sideEffect=()=>setViewLibraryUser(null);
    } else if(s.startsWith("micro_open_shared_")){
      const id=s.replace("micro_open_shared_","");
      target="micro";
      sideEffect=()=>setOpenMicroKey("shared-"+id);
    } else if(s.startsWith("micro_open_")){
      const id=s.replace("micro_open_","");
      target="micro";
      sideEffect=()=>setOpenMicroKey("micro-"+id);
    } else if(s==="capture"&&opts&&(opts.presetMicroId||opts.presetSharedId)){
      target="capture";
      sideEffect=()=>setCapturePreset({microId:opts.presetMicroId||null,sharedId:opts.presetSharedId||null});
    } else if(s==="capture"){
      target="capture";
      sideEffect=()=>setCapturePreset(null);
    } else if(s==="notebook"&&opts&&opts.user){
      target="notebook";
      sideEffect=()=>setOpenUser(opts.user);
    } else if(s==="print_preview"&&opts&&opts.book){
      target="print_preview";
      sideEffect=()=>setPrintBook(opts.book);
    } else if(s==="print_format"&&opts&&opts.book){
      target="print_format";
      sideEffect=()=>setPrintBook(opts.book);
    } else if(s==="print_order"&&opts&&opts.book){
      target="print_order";
      sideEffect=()=>{ setPrintBook(opts.book); if(opts.format) setPrintFormat(opts.format); };
    } else if(s==="filters"&&opts&&opts.highlightId){
      target="filters";
      sideEffect=()=>setHighlightFilterId(opts.highlightId);
    } else if(s==="filters"){
      target="filters";
      sideEffect=()=>setHighlightFilterId(null);
    }
    // flip è già stato azzerato sopra (se c'era), quindi qui conta solo se
    // le due schermate coinvolte fanno parte delle 6 sezioni con la curva —
    // niente più "!flip" da controllare, sennò basterebbe una transizione
    // già in corso per bloccare la successiva.
    const canFlip=target!==curScreen&&SWIPE_SCREENS.includes(target)&&SWIPE_SCREENS.includes(curScreen);
    // "Indietro" non deve lasciarsi dietro una nuova traccia — altrimenti un
    // secondo "indietro" subito dopo riporterebbe alla schermata appena
    // lasciata invece di proseguire più indietro nella cronologia vera.
    if(target!==curScreen&&!(opts&&opts.fromBack)) historyStack.current.push(curScreen);
    if(sideEffect) sideEffect();
    if(canFlip){
      const dir=SWIPE_SCREENS.indexOf(target)>SWIPE_SCREENS.indexOf(curScreen)?"next":"prev";
      const rect=sectionRootRef.current?sectionRootRef.current.getBoundingClientRect():null;
      wipeGeomRef.current={
        W:rect?rect.width:(typeof window!=="undefined"?window.innerWidth:390),
        H:rect?rect.height:(typeof window!=="undefined"?window.innerHeight:700),
      };
      wipeDragRef.current.dir=dir==="next"?-1:1;
      // Nessun montaggio da fare: "target" è già pronto, una delle 6 sezioni
      // sempre vive. Basta assicurarsi che parta nascosta prima che la
      // curva la riveli.
      resetLayerHidden(target);
      setFlip({dir,target});
    } else if(SWIPE_SCREENS.includes(target)){
      // Si torna a una delle 6 sezioni principali da una schermata secondaria
      // (impostazioni, notifiche, un profilo, ecc.) — quella sezione non è mai
      // stata smontata, basta renderla di nuovo visibile: la schermata
      // secondaria (un overlay più sotto nel render, vedi "screen") sparisce
      // da sola perché "screen" non la indica più. L'ultima delle 6 rimasta
      // "attiva sotto" (activeMainRef, può differire da "screen" proprio
      // perché eri su una schermata secondaria) va nascosta esplicitamente,
      // altrimenti resterebbero due sezioni visibili sovrapposte.
      const leavingMain=activeMainRef.current;
      showLayer(target);
      if(leavingMain!==target) hideLayer(leavingMain);
      activeMainRef.current=target;
      setScreenSync(target);
    } else {
      // Si va verso (o tra) schermate fuori dalle 6 con la curva —
      // impostazioni, notifiche, filtri, un profilo, un libro, ecc. Niente
      // curva qui, e niente da tenere caldo: sono raggiunte con un tocco
      // fisico, non uno scorrimento, e va bene ricaricarle ogni volta (così
      // confermato esplicitamente) — un semplice overlay sopra le 6 sezioni,
      // che restano intatte e vive sotto di lui.
      setScreenSync(target);
    }
  },[flip,st.profile.username]);
  // Swipe-right-to-go-back on any screen that isn't one of the six main
  // sections (those instead cycle via the section-to-section gesture below).
  // Delega interamente a go(): stessa logica, stessa garanzia che lo slot
  // attivo resti coerente con "screen", niente da duplicare qui.
  const goBack=useCallback(()=>{
    const prev=historyStack.current.pop();
    go(prev||"feed",{fromBack:true});
  },[go]);
  // ── Swipe-to-turn: horizontal drags move between the six main sections ────
  // (il vero calcolo della curva vive più sopra, in handleMove/applyWipeVisual;
  // qui si segna solo dove il gesto è partito e si gestisce il rilascio)
  const onTouchStart=useCallback((e)=>{
    if(shelfDragFlag.active||textWritingFlag.active) return;
    // Prima, se una transizione precedente stava ancora animando, il nuovo
    // tocco veniva semplicemente ignorato — su uno swipe veloce ripetuto
    // capitava di "perdere" il gesto senza alcun segnale. Ora la transizione
    // a metà si chiude subito (stesso forceFinishFlip usato da go()) e il
    // nuovo gesto parte comunque, tracciato dal primo istante.
    if(flip) forceFinishFlip(flip);
    const tt=e.touches[0];
    wipeDragRef.current={dragging:true,decided:false,startX:tt.clientX,startY:tt.clientY,dir:null,target:null,touchY:0,dx:0,ignore:isInsideHScroll(e.target),vel:null,lastMoveT:e.timeStamp||performance.now(),lastMoveX:tt.clientX};
  },[flip]);
  const onTouchEnd=useCallback((e)=>{
    const dr=wipeDragRef.current;
    const wasActive=dr.dragging;
    dr.dragging=false;
    if(!wasActive||dr.ignore||shelfDragFlag.active||shelfDragFlag.justDragged||textWritingFlag.active) return;
    const tt=e.changedTouches[0];
    if(dr.decided){
      // il trascinamento dal vivo era già partito: completa o annulla da dove si trovava il dito davvero
      finishDragWipe(dr,tt.clientX);
      return;
    }
    // mai diventato un trascinamento dal vivo (gesto troppo corto, verticale,
    // o schermata fuori dalle 6 sezioni) — stesso comportamento di sempre.
    // Si legge screenRef.current (non "screen" catturato al render) perché
    // nel frattempo onTouchStart potrebbe aver già chiuso una transizione
    // precedente e fatto avanzare la sezione corrente.
    const dx=tt.clientX-dr.startX, dy=tt.clientY-dr.startY;
    if(Math.abs(dx)<70||Math.abs(dx)<Math.abs(dy)*1.4) return;
    const curScreen=screenRef.current;
    if(SWIPE_SCREENS.includes(curScreen)){
      const idx=SWIPE_SCREENS.indexOf(curScreen);
      if(dx<0&&idx<SWIPE_SCREENS.length-1) go(SWIPE_SCREENS[idx+1]);
      else if(dx>0&&idx>0) go(SWIPE_SCREENS[idx-1]);
    } else if(dx>0){
      goBack();
    }
  },[go,goBack]);
  // Ogni volta che le ricompense cambiano — sbloccata una nuova, riscattato
  // un obiettivo, equipaggiata una coccarda — le salva sul database. Un
  // unico punto per tutto, così non serve ricordarsi di farlo in ogni posto
  // del codice che le tocca.
  useEffect(()=>{
    if(authStage!=="ready") return;
    const uid=session&&session.user&&session.user.id;
    if(!uid) return;
    updateRewards(uid,st.rewards);
  },[st.rewards,authStage]);
  // Stesso principio delle ricompense: il tema scelto va salvato anche sul
  // database, non solo in locale — altrimenti al prossimo avvio l'app lo
  // ricarica dal profilo vero (rimasto quello vecchio) e la scelta fatta si
  // perde, esattamente il bug segnalato da Matte.
  useEffect(()=>{
    if(authStage!=="ready") return;
    const uid=session&&session.user&&session.user.id;
    if(!uid) return;
    updateProfileTheme(uid,st.myTheme);
  },[st.myTheme,authStage]);
  // Avvisa appena un obiettivo diventa riscattabile, anche senza aprire la
  // schermata Obiettivi da soli — altrimenti l'unico modo per accorgersene
  // era andarci a controllare di persona. Usa le stesse metriche/obiettivi
  // di Goals.jsx; qui si aggiunge solo il "chi è già stato avvisato",
  // salvato in locale per non ripetere lo stesso avviso ad ogni render.
  const notifiedGoalsRef=useRef(null);
  if(notifiedGoalsRef.current===null) notifiedGoalsRef.current=new Set(LS.g("oncer_notified_goals",[]));
  useEffect(()=>{
    if(authStage!=="ready") return;
    const uid=session&&session.user&&session.user.id;
    const metrics=computeMetrics(st);
    const season=getSeasonForMonth(TODAY.getMonth());
    const activeGoals=[...MONTHLY_GOALS,...(QUARTERLY_GOALS[season.q]||[])];
    for(const goal of activeGoals){
      if(!goal.reward) continue;
      if(st.rewards.claimed.includes(goal.id)) continue;
      if(notifiedGoalsRef.current.has(goal.id)) continue;
      if(!goalProgress(goal,metrics).done) continue;
      const reward=REWARDS_CATALOG.find(r=>r.id===goal.reward);
      const rewardName=reward?reward.name:"";
      const rewardEmoji=reward?reward.emoji:"🏆";
      notifiedGoalsRef.current.add(goal.id);
      LS.s("oncer_notified_goals",[...notifiedGoalsRef.current]);
      pushToast(dispatch,tr("goal_unlocked_toast",{name:rewardName}),"success");
      // Oltre al messaggio lampo, resta anche in campanella finché non
      // viene letta — è una notifica "di sistema", non da un altro utente,
      // quindi from_user_id è nullo.
      if(uid){
        notifyGoalUnlocked(uid,{goalId:goal.id,rewardId:goal.reward,name:rewardName,emoji:rewardEmoji}).then(row=>{
          dispatch({type:"ADD_NOTIFICATION_LOCAL",notif:{
            id:row?row.id:`local-${goal.id}-${Date.now()}`,
            type:"goal_unlocked",read:false,
            createdAt:row?row.created_at:new Date().toISOString(),
            data:{goalId:goal.id,rewardId:goal.reward,name:rewardName,emoji:rewardEmoji},
            fromUserId:null,fromUsername:null,fromAvatarUrl:null,fromTheme:"neon",
          }});
        });
      }
    }
  },[st,authStage]);
  // Appena tutti e 3 gli obiettivi giornalieri di oggi risultano completati,
  // segna la giornata come "perfetta" (vedi MARK_DAILY_GOALS_COMPLETE nel
  // reducer, che tiene la serie di giorni consecutivi). Sta qui e non solo
  // in Goals.jsx perché deve accorgersene anche se l'utente non ha aperto
  // la schermata Obiettivi — es. ha completato l'ultimo obiettivo scattando
  // una foto da Capture e non è mai passato da lì.
  useEffect(()=>{
    if(authStage!=="ready") return;
    const metrics=computeMetrics(st);
    const todaysGoals=pickDailyGoals(DAILY_GOALS_POOL,YR*1000+DOY,3);
    const allDone=todaysGoals.length>0&&todaysGoals.every(g=>goalProgress(g,metrics).done);
    if(!allDone) return;
    const prevStreak=st.rewards.dailyStreak||{count:0,lastDay:null,lastYear:null};
    const already=prevStreak.lastDay===DOY&&prevStreak.lastYear===YR;
    if(already) return;
    dispatch({type:"MARK_DAILY_GOALS_COMPLETE"});
    const consecutive=prevStreak.lastYear===YR&&prevStreak.lastDay===DOY-1;
    const newCount=consecutive?prevStreak.count+1:1;
    pushToast(dispatch,tr("goals_daily_streak_toast",{n:newCount}),"success");
  },[st,authStage]);
  // Aggiunge eventuali filtri ufficiali nuovi (come gli "Oncer Setup")
  // che mancano ancora a chi ha già l'elenco filtri salvato da prima —
  // una volta sola per sessione, non a ogni render.
  useEffect(()=>{
    if(authStage!=="ready") return;
    dispatch({type:"ENSURE_OFFICIAL_FILTERS"});
  },[authStage]);
  // Registra il dispositivo per le notifiche push (Firebase Cloud Messaging
  // via il plugin Capacitor) — solo su app nativa, una volta sola quando
  // l'utente risulta autenticato. Sul sito oncer.app normale (o durante
  // npm run build/dev nel browser) il plugin nativo non esiste, quindi non
  // si tenta nemmeno: Capture.jsx usa lo stesso controllo per la fotocamera.
  useEffect(()=>{
    if(authStage!=="ready") return;
    const uid=session&&session.user&&session.user.id;
    if(!uid) return;
    if(!Capacitor.isNativePlatform()) return;
    (async()=>{
      try{
        let perm=await PushNotifications.checkPermissions();
        if(perm.receive!=="granted") perm=await PushNotifications.requestPermissions();
        if(perm.receive!=="granted") return;
        PushNotifications.addListener("registration",token=>{ savePushToken(uid,token.value); });
        await PushNotifications.register();
      }catch(err){ console.error("Oncer: registrazione notifiche push fallita —",err.message); }
    })();
  },[authStage]);
  // Deep link di ritorno dopo il pagamento Stripe (02/09/2026, bug segnalato da Matte: "Torna a
  // Oncer" nelle pagine stampa-completata.html/stampa-annullata.html apriva solo la root del
  // sito nel browser esterno, mai l'app nativa, perché non esisteva nessun deep link configurato
  // — vedi AndroidManifest.xml, nuovo intent-filter con scheme "oncer"). Quelle pagine ora
  // puntano a oncer://print-success e oncer://print-cancelled: quando Android/iOS apre l'app
  // tramite quel link, Capacitor lo consegna qui.
  useEffect(()=>{
    if(!Capacitor.isNativePlatform()) return;
    const subPromise=CapApp.addListener("appUrlOpen",({url})=>{
      try{
        const path=String(url||"").split("://")[1]||"";
        const screen=path.split("?")[0].replace(/\/$/,"");
        if(screen==="print-success"){
          pushToast(dispatch,tr("print_order_confirmed_toast"),"success");
          go("print_hub");
        } else if(screen==="print-cancelled"){
          pushToast(dispatch,tr("print_order_cancelled_toast"));
          go("print_hub");
        }
      }catch(err){ console.error("Oncer: gestione deep link fallita —",err.message); }
    });
    return ()=>{ Promise.resolve(subPromise).then(h=>h&&h.remove&&h.remove()).catch(()=>{}); };
  },[]);
  const t=TH[st.myTheme]||TH.neon;
  if(passwordRecovery) return <PasswordRecoveryView onDone={()=>{ setPasswordRecovery(false); }}/>;
  // t.ac/t.ac2 vengono da st.myTheme, popolato subito all'avvio da
  // localStorage ("oncer_theme", vedi LS.g più su e LS.s in reducer.js) —
  // quindi il logo animato si colora già col tema scelto l'ultima volta
  // dall'utente, senza dover aspettare il profilo dal server. Chi non ha
  // ancora un tema salvato (primo avvio in assoluto) vede il rosa/viola di
  // default, dato che TH.neon è il fallback sia qui che in LS.g.
  if(authStage==="checking") return <AnimatedSplash ac={t.ac} ac2={t.ac2} onDone={()=>{splashTypingDoneRef.current=true;}}/>;
  if(authStage==="out") return(
    <Onboarding startRecovering={needsProfileSetup} onDone={({username,theme,bio,notif})=>{
      dispatch({type:"SET_THEME",theme});
      dispatch({type:"SET_PROFILE",data:{username,bio:bio||""}});
      if(notif) dispatch({type:"UPDATE_SETTINGS",data:{notifTime:notif}});
      setNeedsProfileSetup(false);
      setAuthStage("ready");
      let tourSeen=null;
      try{ tourSeen=localStorage.getItem("oncer_tour_seen"); }catch(e){}
      if(!tourSeen) setShowGuidedTour(true);
    }}/>
  );
  const cp={t,theme:st.myTheme,st,dispatch,go,goBack,userId:session&&session.user&&session.user.id,userEmail:session&&session.user&&session.user.email};
  const renderScreenByName=(name)=>{
    switch(name){
      case "feed":          return <FeedView         {...cp}/>;
      case "capture":       return <CaptureView      {...cp} capturePreset={capturePreset} clearCapturePreset={()=>setCapturePreset(null)} onNavVisible={setCaptureNavVisible}/>;
      case "library":       return <LibraryView      {...cp} username={viewLibraryUser}/>;
      case "micro":         return <MicroView        {...cp} openMicroKey={openMicroKey} clearOpenMicroKey={()=>setOpenMicroKey(null)}/>;
      case "notebook":      return <NotebookView     {...cp} openUser={openUser} clearOpenUser={()=>setOpenUser(null)}/>;
      case "discover":      return <DiscoverView     {...cp} discoverTab={discoverTab} setDiscoverTab={setDiscoverTab}/>;
      case "profile":       return <ProfileView      {...cp} username={viewUser} onNavTheme={setProfileNavTheme}/>;
      case "settings":      return <SettingsView     {...cp}/>;
      case "shelf_editor":  return <ShelfEditorView   {...cp}/>;
      case "notifications": return <NotificationsView {...cp}/>;
      case "moderation": return <ModerationView {...cp}/>;
      case "subscription":  return <SubscriptionView {...cp}/>;
      case "print_hub":     return <PrintHubView    {...cp}/>;
      // Sezione stampa ancora "Coming soon" per tutti — TRANNE per gli account
      // admin (st.profile.isAdmin), che possono aprire le viste reali per
      // testare l'intero flusso (wizard formato → preventivo live → PDF →
      // pagamento) prima che il partner di stampa e Stripe siano davvero
      // collegati con chiavi vere. printBook/printFormat arrivano da
      // PrintHubView via go("print_preview",{book}) →
      // go("print_format",{book}) → go("print_order",{book,format}).
      case "print_preview": return st.profile.isAdmin ? <PrintPreviewView {...cp} book={printBook}/> : <PrintComingSoonView t={t} go={go}/>;
      case "print_format":  return st.profile.isAdmin ? <PrintFormatView  {...cp} book={printBook}/> : <PrintComingSoonView t={t} go={go}/>;
      case "print_order":   return st.profile.isAdmin ? <PrintOrderView   {...cp} book={printBook} format={printFormat}/> : <PrintComingSoonView t={t} go={go}/>;
      case "filters":       return <FiltersView t={t} st={st} dispatch={dispatch} go={go} pickMode={false} highlightId={highlightFilterId}/>;
      case "filter_create": return <FilterCreateView t={t} st={st} dispatch={dispatch} go={go} userId={cp.userId}/>;
      case "goals":         return <GoalsView t={t} st={st} dispatch={dispatch} go={go}/>;
      case "year_close":    return <YearCloseView t={t} st={st} go={go}/>;
      case "oncer_future":  return <OncerFutureView t={t} st={st} dispatch={dispatch} go={go} userId={cp.userId}/>;
      case "rewards":       return <RewardsView t={t} st={st} dispatch={dispatch} go={go}/>;
      case "privacy":       return <PrivacyPolicyView t={t} st={st} dispatch={dispatch} go={go}/>;
      case "terms":         return <TermsView t={t} st={st} dispatch={dispatch} go={go}/>;
      default: return null;
    }
  };
  return(
    <div ref={setSectionRoot} style={{height:"100%",position:"relative",background:t.bg,overflow:"hidden"}} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* Le 6 sezioni principali restano SEMPRE montate, tutte e sei, per
          tutta la sessione — mai smontate/rimontate passando dall'una
          all'altra. "screen" decide solo quale delle 6 è quella attiva e
          visibile in questo momento (o, se sei su una schermata secondaria
          — l'overlay qui sotto — quale resta ferma sotto di lei, coperta ma
          viva). Zero rimontaggi, zero nuove chiamate di rete, in qualunque
          ordine tu le visiti e dopo quanto tempo vuoi. */}
      {SWIPE_SCREENS.map(name=>{
        const isActive=name===screen;
        const cls="wipe-layer"+(isActive?(flip?" wipe-transitioning":""):" wipe-over");
        return(
          <div key={name} className={cls} ref={screenRefCallbacks[name]}>
            {renderScreenByName(name)}
          </div>
        );
      })}
      {/* Tutto ciò che NON è una delle 6 (impostazioni, notifiche, filtri, un
          profilo aperto, ecc.) — un semplice overlay sopra le 6, montato solo
          quando serve e smontato quando si torna a una delle 6. Raggiunte
          con un tocco fisico, non uno scorrimento: nessuna curva, nessuna
          cache, va bene ricaricarle ogni volta. */}
      {!SWIPE_SCREENS.includes(screen)&&(
        <div className="wipe-layer wipe-secondary-overlay">
          {renderScreenByName(screen)}
        </div>
      )}
      {flip&&(
        <svg style={{position:"absolute",inset:0,width:"100%",height:"100%",zIndex:6,pointerEvents:"none"}} viewBox={`0 0 ${wipeGeomRef.current.W||390} ${wipeGeomRef.current.H||700}`} preserveAspectRatio="none">
          {/* transition sull'opacità: durante il trascinamento dal vivo/l'animazione
              automatica applyWipeVisual scrive opacity="1" ad ogni fotogramma (nessuna
              dissolvenza visibile, cambia troppo spesso perché si noti), ma quando
              settleDivider scrive opacity="0" UNA sola volta a fine transizione, questa
              transizione la fa sfumare invece di sparire di colpo. */}
          <path ref={setDividerRef} d="" fill="none" stroke={t.ac} strokeWidth="1.2" opacity="0" style={{transition:"opacity .15s ease"}}/>
        </svg>
      )}
      {/* Barra di navigazione — istanza UNICA per tutte e 6 le sezioni (vedi
          nota sopra vicino a captureNavVisible/profileNavTheme). zIndex:30
          (dentro BNav stesso) la tiene sopra a wipe-over (5), alla curva
          svg (6) e persino al wipe-secondary-overlay (9) — ma qui la
          mostriamo SOLO quando "screen" è una delle 6, altrimenti resterebbe
          visibile anche sopra Libreria/Filtri/Obiettivi, che hanno già la
          propria barra locale (nessuna curva lì, nessun conflitto). */}
      {SWIPE_SCREENS.includes(screen)&&(screen!=="capture"||captureNavVisible)&&(
        <BNav fixed t={screen==="profile"?(profileNavTheme||t):t} items={mainNavItems(screen,t,go,st)}/>
      )}
      <ToastDisplay toast={st.toast} dispatch={dispatch}/>
      {showGuidedTour&&(
        <GuidedTour go={go} t={t} onFinish={()=>{
          try{ localStorage.setItem("oncer_tour_seen","1"); }catch(e){}
          setShowGuidedTour(false);
          go("feed");
        }}/>
      )}
      {showSwipeHint&&(
        <div style={{position:"fixed",inset:0,zIndex:250,background:"rgba(0,0,0,.6)",display:"flex",alignItems:"center",justifyContent:"center",padding:28}} onClick={()=>{localStorage.setItem("oncer_swipehint_seen","1");setShowSwipeHint(false);}}>
          <div style={{background:"#fff",borderRadius:20,padding:"26px 22px",maxWidth:300,textAlign:"center",boxShadow:"0 20px 50px rgba(0,0,0,.4)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:38,marginBottom:12}}>👉</div>
            <div style={{fontSize:16,fontWeight:800,color:"#1a1a1a",fontFamily:"Inter,sans-serif",marginBottom:9}}>{tr("app_swipehint_title")}</div>
            <div style={{fontSize:13,color:"#666",fontFamily:"Inter,sans-serif",lineHeight:1.6,marginBottom:8}}>{tr("app_swipehint_body1")}</div>
            <div style={{fontSize:13,color:"#666",fontFamily:"Inter,sans-serif",lineHeight:1.6,marginBottom:20}}>{tr("app_swipehint_body2")}</div>
            <Btn fn={()=>{localStorage.setItem("oncer_swipehint_seen","1");setShowSwipeHint(false);}} style={{background:"linear-gradient(135deg,#FF2D6B,#7B2FFF)",color:"#fff",padding:"12px 28px",borderRadius:40,fontSize:13,fontWeight:700,fontFamily:"Inter,sans-serif",display:"inline-block"}}>{tr("app_swipehint_cta")}</Btn>
          </div>
        </div>
      )}
    </div>
  );
}
